<template>
  <div
    ref="progressBarRootEl"
    class="progress-bar-root flex items-center"
    :style="{
      height: `${height}px`,
      width: '100%' /* Takes full width of its parent container */
    }"
  >
    <div
      class="progress-bar-inner flex"
      :style="{
        width: calculatedInnerContainerWidth,
        height: '100%' /* Fills the height of the root element */,
        columnGap: `${GAP_PX}px` /* Space between blocks */
      }"
    >
      <div
        v-for="(block, index) in blocks"
        :key="index"
        :style="{ width: `${BLOCK_WIDTH_PX}px` }"
        :class="[
          'h-full',
          'rounded-md',
          index < activeBlocksCount ? props.activeColor : props.inactiveColor
        ]"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

// --- Component Props ---
export interface ProgressBarProps {
  progress: number // Progress percentage (0-100)
  height?: number // Height of the progress bar in pixels
  activeColor: string // Tailwind class for active blocks
  inactiveColor: string // Tailwind class for inactive blocks
}

const props = withDefaults(defineProps<ProgressBarProps>(), {
  height: 35, // Default height if not provided
  activeColor: 'bg-green-500', // Default activeColor if not provided
  inactiveColor: 'bg-gray-300' // Default inactiveColor if not provided
})

// --- Constants ---
const GAP_PX = 5 // Gap between progress blocks in pixels
const BLOCK_WIDTH_PX = 5 // Width of each individual progress block in pixels

// --- Reactive State ---
const progressBarRootEl = ref<HTMLElement | null>(null) // Ref for the root element of the component
const currentRootWidth = ref(0) // Stores the actual width of the root element in pixels

// --- Resize Observer ---
// Observes the root element for width changes and updates `currentRootWidth`
let resizeObserver: ResizeObserver | null = null

const updateRootWidth = () => {
  if (progressBarRootEl.value) {
    currentRootWidth.value = progressBarRootEl.value.offsetWidth
  }
}

onMounted(() => {
  if (progressBarRootEl.value) {
    // Get initial width
    updateRootWidth()

    // Set up ResizeObserver to listen for width changes
    resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to avoid "ResizeObserver loop limit exceeded" errors
      // and ensure layout calculations are based on the latest DOM state.
      requestAnimationFrame(updateRootWidth)
    })
    resizeObserver.observe(progressBarRootEl.value)
  }
})

onUnmounted(() => {
  // Clean up ResizeObserver when the component is unmounted
  if (resizeObserver && progressBarRootEl.value) {
    resizeObserver.unobserve(progressBarRootEl.value)
  }
  resizeObserver = null
})

// --- Computed Properties ---

/**
 * Calculates the maximum number of blocks that can fit into the current width of the progress bar.
 */
const maxBlocksCount = computed(() => {
  if (currentRootWidth.value === 0) return 0
  // Formula: (total available width + one gap) / (width of one block + one gap)
  // This accounts for the gaps between blocks.
  return Math.floor((currentRootWidth.value + GAP_PX) / (BLOCK_WIDTH_PX + GAP_PX))
})

/**
 * Calculates the number of blocks that should be active (colored) based on the progress percentage.
 */
const activeBlocksCount = computed(() => {
  // Ensure progress is clamped between 0 and 100
  const normalizedProgress = Math.max(0, Math.min(100, props.progress))
  return Math.round((normalizedProgress / 100) * maxBlocksCount.value)
})

/**
 * Calculates the exact width the inner container (holding the blocks) should have.
 * This ensures that the blocks and gaps fit perfectly without extra space or wrapping.
 */
const calculatedInnerContainerWidth = computed(() => {
  if (maxBlocksCount.value <= 0) return '0px'
  // Total width = (number of blocks * block width) + (number of gaps * gap width)
  // Number of gaps is maxBlocksCount - 1 (if there's at least one block)
  const width =
    maxBlocksCount.value * BLOCK_WIDTH_PX +
    (maxBlocksCount.value > 0 ? (maxBlocksCount.value - 1) * GAP_PX : 0)
  return `${width}px`
})

/**
 * Generates an array to be used with v-for for rendering the blocks.
 * The length of the array determines how many block divs are rendered.
 */
const blocks = computed(() => {
  // We only need an array of the correct length; the content of items doesn't matter.
  return Array.from({ length: maxBlocksCount.value })
})
</script>

<style scoped></style>
